import { useMemo, useState } from "react";

import { getFAQ } from "../lib/faq";

import Input from "../components/input";

import styles from "./faq.module.scss";
import config from "../config";
import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";

const FAQItem = ({ title, description }) => {
  return (
    <div className={styles.FAQItem}>
      <div className={styles.Title}>{title}</div>
      <p className={styles.Description}>{description}</p>
    </div>
  );
};

const FAQCategory = ({ title, items }) => {
  if (items.length === 0) return null;

  return (
    <div className={styles.FAQCategory}>
      <h3>{title}</h3>

      {items.map((item) => (
        <FAQItem {...item} key={item.title} />
      ))}
    </div>
  );
};

const FAQSection = ({ title, slug, categories }) => {
  const categoriesNotEmpty = categories.filter(
    (category) => category.faqItemsCollection.items.length > 0
  );

  if (categoriesNotEmpty.length === 0) {
    return null;
  }

  return (
    <div id={slug} className={styles.FAQSection}>
      <h2>{title}</h2>

      {categories.map((category) => (
        <FAQCategory
          key={category.title}
          title={category.title}
          slug={category.slug}
          items={category.faqItemsCollection.items}
        />
      ))}
    </div>
  );
};

function matchInString(search, str) {
  return str.toLowerCase().includes(search.toLowerCase().trim());
}

function searchItems(search, items) {
  return items.filter(
    (item) =>
      matchInString(search, item.title) ||
      matchInString(search, item.description)
  );
}

function searchFaq(search, faq) {
  // main content, for each section's category search in items.
  for (const section of faq) {
    for (const category of section.faqCategoriesCollection.items) {
      category.faqItemsCollection.items = searchItems(
        search,
        category.faqItemsCollection.items
      );
    }
  }

  return faq;
}

export default function FaqPage({ faq, seo }) {
  const [search, setSearch] = useState("");

  const filteredSections = useMemo(() => {
    if (search === "") return faq;

    return searchFaq(search, JSON.parse(JSON.stringify(faq)));
  }, [search, faq]);

  const scrollToSection = (event, id) => {
    event.preventDefault();

    const el = document.querySelector(id);
    if (el) {
      window.scrollTo({
        top: el.offsetTop,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="container">
      <SeoMeta {...seo} />
      <div className={styles.FAQWrapper}>
        <div className={styles.Nav}>
          <h1 className={styles.PageTitle}>FAQ</h1>
          <div className={styles.FAQMenuContainer}>
            <ul className={styles.FAQMenu}>
              {faq.map((section) => (
                <li key={section.slug}>
                  <a
                    href={`#${section.slug}`}
                    onClick={(e) => scrollToSection(e, `#${section.slug}`)}
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>

            <div className={styles.SearchWrapper}>
              <Input
                className={styles.SearchInput}
                type="text"
                value={search}
                placeholder="Search"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={styles.FAQContent}>
          {filteredSections.length === 0 && <p>No results found.</p>}

          {filteredSections.map((faqSection) => (
            <FAQSection
              key={faqSection.slug}
              title={faqSection.title}
              slug={faqSection.slug}
              categories={faqSection.faqCategoriesCollection.items}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export async function getStaticProps() {
  try {
    const [faq, seo] = await Promise.all([
      getFAQ(),
      getSEOPageProps({ resolvedUrl: "/faq" })
    ]);

    return {
      props: {
        faq,
        seo
      },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
  }
}
